import { createRouter, createWebHistory } from 'vue-router'
import type { UserRole } from '@/types'
import { ROUTE_NAMES } from '@/constants'
import { useAuthStore } from '@/stores/auth'

const ROLES_ALL: UserRole[] = ['admin', 'editor', 'writer']
const ROLES_ADMIN_EDITOR: UserRole[] = ['admin', 'editor']
const ROLES_ADMIN_ONLY: UserRole[] = ['admin']

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes: [
    {
      path: '/login',
      name: ROUTE_NAMES.LOGIN,
      component: () => import('@/views/LoginView.vue'),
      meta: { guest: true },
    },
    {
      path: '/',
      component: () => import('@/views/AppLayout.vue'),
      meta: { requiresAuth: true },
      children: [
        {
          path: '',
          name: ROUTE_NAMES.DASHBOARD,
          component: () => import('@/views/DashboardView.vue'),
          meta: { roles: ROLES_ALL },
        },
        {
          path: 'style-profiling',
          name: ROUTE_NAMES.STYLE_PROFILING,
          component: () => import('@/views/StyleProfilingView.vue'),
          meta: { roles: ROLES_ADMIN_EDITOR },
        },
        {
          path: 'news-input',
          name: ROUTE_NAMES.NEWS_INPUT,
          component: () => import('@/views/NewsInputView.vue'),
          meta: { roles: ROLES_ALL },
        },
        {
          path: 'content-generation',
          name: ROUTE_NAMES.CONTENT_GENERATION,
          component: () => import('@/views/ContentGenerationView.vue'),
          meta: { roles: ROLES_ALL },
        },
        {
          path: 'publishing',
          name: ROUTE_NAMES.PUBLISHING,
          component: () => import('@/views/PublishingView.vue'),
          meta: { roles: ROLES_ADMIN_EDITOR },
        },
        {
          path: 'content-archive',
          name: ROUTE_NAMES.CONTENT_ARCHIVE,
          component: () => import('@/views/ContentArchiveView.vue'),
          meta: { roles: ROLES_ALL },
        },
        {
          path: 'user-management',
          name: ROUTE_NAMES.USER_MANAGEMENT,
          component: () => import('@/views/UserManagementView.vue'),
          meta: { roles: ROLES_ADMIN_ONLY },
        },
      ],
    },
    {
      path: '/presentation',
      name: ROUTE_NAMES.PRESENTATION,
      component: () => import('@/views/PresentationView.vue'),
    },
  ],
})

router.beforeEach(async (to, _from, next) => {
  const auth = useAuthStore()
  if (!auth.user && auth.isLoading) {
    await auth.init()
  }
  if (to.meta.requiresAuth && !auth.isAuthenticated) {
    next({ name: ROUTE_NAMES.LOGIN })
    return
  }
  if (to.meta.guest && auth.isAuthenticated) {
    next({ name: ROUTE_NAMES.DASHBOARD })
    return
  }
  const roles = to.meta.roles as UserRole[] | undefined
  if (roles?.length && auth.user && !roles.includes(auth.user.role)) {
    next({ name: ROUTE_NAMES.DASHBOARD })
    return
  }
  next()
})

export default router
